import styled from '@emotion/styled';
import { motion } from 'framer-motion';

/**
 * BasePage.styles.ts
 * 
 * Gemeinsame Basis-Styles für alle Seiten (Impressum, DSGVO, etc.)
 * 
 * Best Practice Architektur:
 * - BasePage.styles.ts: Gemeinsame Styles für alle Pages
 * - Page-spezifische Modifier können in einzelnen Page-Styles sein wenn nötig
 * - Alle Pages importieren BasePage.styles.ts
 */

// Basis Page Wrapper - verwendet Theme-Farben
export const PageWrapper = styled.div<{ colors: any }>`
  min-height: 100vh;
  background: ${props => props.colors.bg};
  position: relative;
  overflow-x: hidden;
  padding-top: 80px; /* Platz für fixed Header */
  
  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

// Content Container - zentriert mit max-width
export const Container = styled.div`
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  padding: 60px 40px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 30px 15px;
  }
`;

// Section Wrapper
export const Section = styled(motion.section)`
  padding: 0;
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

// Haupttitel der Seite
export const PageTitle = styled(motion.h1)<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  color: ${props => props.colors.primary};
  margin-bottom: 40px;
  text-align: center;
  transition: color 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: clamp(1.8rem, 5vw, 2.5rem);
    margin-bottom: 30px;
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.5rem, 6vw, 2rem);
    margin-bottom: 25px;
  }
`;

// Section Titel (h2)
export const SectionTitle = styled(motion.h2)<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.3rem, 2.5vw, 1.8rem);
  font-weight: 600;
  color: ${props => props.colors.primary};
  margin-top: 40px;
  margin-bottom: 20px;
  transition: color 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: clamp(1.2rem, 3vw, 1.5rem);
    margin-top: 35px;
    margin-bottom: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.1rem, 4vw, 1.3rem);
    margin-top: 30px;
    margin-bottom: 15px;
  }
`;

// Content Block (Paragraph/Text)
// WICHTIG FÜR TEXT-TO-SPEECH: Diese Komponente MUSS IMMER data-text-element="true" haben!
// Regel: Jede Verwendung von <S.ContentBlock> MUSS data-text-element="true" enthalten
export const ContentBlock = styled(motion.div)<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.85rem, 1.2vw, 1rem);
  line-height: 1.8;
  color: ${props => props.colors.textPrimary};
  white-space: pre-line;
  transition: color 0.3s ease;
  margin-bottom: 20px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  
  strong {
    font-weight: 600;
    color: ${props => props.colors.primary};
  }
  
  a {
    color: ${props => props.colors.primary};
    text-decoration: underline;
    transition: all 0.3s ease;
    word-break: break-all;
    
    &:hover {
      color: ${props => props.colors.primaryHover};
      text-shadow: 0 0 10px rgba(230, 0, 126, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    font-size: clamp(0.9rem, 2vw, 1rem);
    margin-bottom: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: clamp(0.85rem, 2.5vw, 0.95rem);
    margin-bottom: 15px;
  }
`;

// Paragraph (einfacher Text)
// WICHTIG FÜR TEXT-TO-SPEECH: Diese Komponente MUSS IMMER data-text-element="true" haben!
// Regel: Jede Verwendung von <S.Paragraph> oder <S.ContentBlock> MUSS data-text-element="true" enthalten
// Beispiel: <S.Paragraph colors={colors} data-text-element="true">...</S.Paragraph>
export const Paragraph = styled(ContentBlock)`
  /* Erbt alle Styles von ContentBlock */
`;

// Address Paragraph - kleiner und unauffälliger
export const AddressParagraph = styled(Paragraph)<{ colors: any }>`
  font-size: clamp(0.75rem, 1vw, 0.85rem);
  color: ${props => props.colors.textSecondary};
  margin-bottom: 8px;
  
  @media (max-width: 480px) {
    font-size: clamp(0.7rem, 2vw, 0.8rem);
  }
`;

// Contact Info Row - für gleichmäßige Ausrichtung
export const ContactRow = styled.div`
  margin-bottom: 20px;
  
  span:first-child {
    display: block;
    margin-bottom: 10px;
    font-weight: 500;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 15px;
  }
`;

// Spam-Schutz: Reveal Button
export const RevealButton = styled.button<{ colors: any }>`
  background: transparent;
  border: 2px solid ${props => props.colors.primary};
  color: ${props => props.colors.primary};
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.85rem, 1vw, 0.95rem);
  font-weight: 400;
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: 10px;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.colors.primary};
    color: ${props => props.colors.bg === '#1a1a1a' ? '#fff' : '#fff'};
  }
  
  &:focus-visible {
    outline: 3px solid #00d9ff;
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(0, 217, 255, 0.3);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(0.8rem, 2vw, 0.9rem);
    padding: 6px 14px;
    margin-left: 5px;
  }
`;

// Revealed Content (Email/Phone nach Click)
export const RevealedContent = styled.span<{ colors: any }>`
  color: ${props => props.colors.primary};
  font-weight: 500;
  word-break: break-all;
  
  a {
    color: ${props => props.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// Success Message (nach Copy)
export const SuccessMessage = styled.span<{ colors: any; show: boolean }>`
  display: ${props => props.show ? 'inline-block' : 'none'};
  color: ${props => props.colors.primary};
  font-size: clamp(0.8rem, 1vw, 0.9rem);
  margin-left: 10px;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: clamp(0.75rem, 2vw, 0.85rem);
    margin-left: 5px;
    display: ${props => props.show ? 'block' : 'none'};
    margin-top: 5px;
  }
`;

// Intro Text (für Datenschutz)
export const Intro = styled(Paragraph)<{ colors: any }>`
  margin-bottom: 40px;
  text-align: left;

  @media (max-width: 768px) {
    margin-bottom: 35px;
  }
`;

// Last Updated (für DSGVO)
export const LastUpdated = styled.div<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.8rem, 1vw, 0.9rem);
  color: ${props => props.colors.textSecondary};
  margin-bottom: 30px;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: clamp(0.75rem, 2vw, 0.85rem);
  }
`;

// Styled Link (für externe Links)
export const StyledLink = styled.a<{ colors: any }>`
  color: ${props => props.colors.primary};
  text-decoration: underline;
  transition: all 0.3s ease;
  word-break: break-all;
  
  &:hover {
    text-shadow: 0 0 10px rgba(230, 0, 126, 0.6);
  }
`;

